import { useEffect, useMemo, useState } from "react";
import { agentRequiredFieldsPath, mutate, useApi } from "../api/client";
import type { Session } from "../api/auth";
import type { RequirementCoverage, RequiredFieldsResponse } from "../api/types";
import { Empty, ErrorState, Loading } from "./States";

/**
 * Enable or disable the fields an agent is scored on.
 *
 * The fill rate sits beside every switch on purpose. Enabling a field that is
 * populated on 8% of runs is a decision to fail 92% of this agent's
 * extractions, and that should be visible at the moment it is made rather than
 * discovered from a dashboard a week later.
 *
 * Nothing here changes the confidence gate. The gate keeps its own hardcoded
 * checks and keeps producing the same score; this decides what is recorded as
 * required, which is what agent-level scoring is computed from.
 */
/**
 * The agent's score: the share of its runs that produced every field it is
 * scored on.
 *
 * Shown with its own denominator rather than as a bare percentage. "42.4%" and
 * "147 of 347 runs" say the same thing, but only the second survives an agent
 * with four runs, where a percentage implies a precision the sample does not
 * have.
 */
function ScorePanel({
  stored,
  preview,
  previewing,
  emptySelection,
}: {
  stored?: RequirementCoverage;
  preview?: RequirementCoverage;
  previewing: boolean;
  emptySelection: boolean;
}) {
  if (!stored?.defined) {
    return (
      <div className="notice">
        No fields are scored for this agent yet, so it has no score. Enable at
        least one below.
      </div>
    );
  }
  if (stored.runs === 0) {
    return (
      <div className="notice">
        This agent has no extractions on record, so there is nothing to score
        against yet. The fields below can still be set.
      </div>
    );
  }

  const tone = (v: number | null) =>
    v === null ? "" : v >= 95 ? "good" : v >= 70 ? "warning" : "critical";
  const delta =
    preview && preview.score !== null && stored.score !== null
      ? Math.round((preview.score - stored.score) * 10) / 10
      : null;

  return (
    <div className="score-panel">
      <div className="score-main">
        <div className="tile-label">Score — required fields extracted</div>
        <div className={`score-value ${tone(stored.score)}`}>
          {stored.score === null ? "—" : `${stored.score}%`}
        </div>
        <div className="tile-note">
          {stored.complete} of {stored.runs} runs produced every scored field
        </div>
      </div>

      {emptySelection ? (
        <div className="score-preview muted">
          Nothing selected — an agent with no scored fields has no score.
        </div>
      ) : previewing ? (
        <div className="score-preview muted">Recalculating…</div>
      ) : preview ? (
        <div className="score-preview">
          <div className="tile-label">With your unsaved changes</div>
          <div className={`score-value ${tone(preview.score)}`}>
            {preview.score === null ? "—" : `${preview.score}%`}
            {delta !== null && delta !== 0 ? (
              <span className={`score-delta ${delta > 0 ? "good" : "critical"}`}>
                {delta > 0 ? "+" : ""}
                {delta}
              </span>
            ) : null}
          </div>
          <div className="tile-note">
            {preview.complete} of {preview.runs} runs — not saved yet
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function ScoredFields({ documentType }: { documentType: string }) {
  const path = agentRequiredFieldsPath(documentType);
  const { data, error, loading, reload } = useApi<RequiredFieldsResponse>(path);

  // The session, for the caller's role. Fetched here rather than threaded down
  // from App because this is the only screen below the nav that needs it, and a
  // context for one consumer is machinery without a customer.
  const { data: session } = useApi<Session>("/api/orchestrator/auth/session");
  const isAdmin = session?.role === "admin";

  // Local until Save. A switch that wrote immediately would mean a misclick
  // silently changes how every future document of this type is scored.
  const [draft, setDraft] = useState<Set<string> | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const stored = useMemo(
    () => new Set((data?.fields ?? []).filter((f) => f.required).map((f) => f.field)),
    [data],
  );

  // Re-seed the draft whenever the server's answer changes, which covers both
  // the first load and the reload after a save.
  useEffect(() => {
    if (data) setDraft(new Set(stored));
  }, [data, stored]);

  const current = draft ?? stored;
  const changed = useMemo(() => {
    const added = [...current].filter((f) => !stored.has(f));
    const removed = [...stored].filter((f) => !current.has(f));
    return { added, removed, count: added.length + removed.length };
  }, [current, stored]);

  /**
   * The score the current selection would produce, fetched while it is still
   * unsaved.
   *
   * A second request rather than a client-side calculation because the answer
   * is per extraction: whether each of 347 runs produced every enabled field,
   * with aliases and minimum lengths applied. The browser has aggregate fill
   * rates, and those cannot be multiplied together to get it -- two fields each
   * filled on 96% of runs might fail the same 4% or different ones.
   *
   * `only=coverage` so this skips the candidate list and the agent-service
   * schema call, neither of which changes between toggles.
   */
  const previewPath =
    changed.count > 0 && current.size > 0
      ? `${path}?only=coverage&enabled=${encodeURIComponent([...current].join(","))}`
      : null;
  const { data: previewData, loading: previewing } =
    useApi<{ coverage: RequirementCoverage }>(previewPath);

  const stored_cov = data?.coverage;
  const preview_cov = previewData?.coverage;

  const toggle = (field: string) => {
    setSaved(false);
    setSaveError(null);
    setDraft((prev) => {
      const next = new Set(prev ?? stored);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await mutate(path, "PUT", { fields: [...current] });
      setSaved(true);
      reload();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="card"><Loading what="scored fields" /></div>;
  if (error)
    return (
      <div className="card">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  if (!data) return null;

  return (
    <div className="card">
      <div className="card-head bordered">
        <span className="card-title">Scored fields</span>
        <span className="muted">
          {data.defined ? (
            <>
              v{data.version}
              {data.updated_by ? ` · last set by ${data.updated_by}` : null}
            </>
          ) : (
            "not yet defined for this agent"
          )}
        </span>
      </div>

      <div className="card-body">
        <p className="page-sub" style={{ marginTop: 0 }}>
          The fields this agent is scored on. Fill rate is measured across every
          extraction it has run — enabling a field the agent rarely produces
          will fail most of its documents.
        </p>

        <ScorePanel
          stored={stored_cov}
          preview={changed.count > 0 ? preview_cov : undefined}
          previewing={previewing && changed.count > 0}
          emptySelection={current.size === 0}
        />

        {stored_cov?.by_field?.length ? (
          <div className="req-breakdown">
            {stored_cov.by_field.map((f) => (
              <div key={f.field} className="req-row">
                <span className="mono">{f.field}</span>
                <span className="req-bar">
                  <span
                    className="req-fill"
                    style={{
                      width: `${f.rate ?? 0}%`,
                      background:
                        (f.rate ?? 0) >= 95
                          ? "var(--good)"
                          : (f.rate ?? 0) >= 70
                            ? "var(--warning)"
                            : "var(--critical)",
                    }}
                  />
                </span>
                <span className="num muted">
                  {f.rate === null ? "—" : `${f.rate}%`}
                </span>
              </div>
            ))}
            {/* The gap between the weakest field and the overall score is the
                thing worth understanding: the score is all-or-nothing per run,
                so it is always at or below the worst field, and lower still
                when different runs miss different fields. */}
            <p className="muted" style={{ fontSize: 12, margin: "6px 2px 0" }}>
              Each bar is the share of runs producing that field on its own. The
              score above counts only runs that produced <em>all</em> of them.
            </p>
          </div>
        ) : null}

        {!isAdmin ? (
          <div className="notice">
            Only an administrator can change which fields are scored. The
            switches below show the current setting.
          </div>
        ) : null}

        {data.fields.length === 0 ? (
          <Empty
            title="No fields to choose from"
            hint="This agent has produced no extractions and its schema could not be read."
          />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col" style={{ width: 70 }}>Scored</th>
                  <th scope="col">Field</th>
                  <th scope="col" className="num">Fill rate</th>
                  <th scope="col" className="num">Populated</th>
                  <th scope="col">Evidence</th>
                </tr>
              </thead>
              <tbody>
                {data.fields.map((f) => {
                  const on = current.has(f.field);
                  const dirty = on !== stored.has(f.field);
                  return (
                    <tr key={f.field} className={dirty ? "row-dirty" : undefined}>
                      <td>
                        <input
                          type="checkbox"
                          checked={on}
                          disabled={!isAdmin || saving}
                          onChange={() => toggle(f.field)}
                          aria-label={`Score ${documentType} on ${f.field}`}
                          title={
                            isAdmin
                              ? undefined
                              : "Administrator accounts only"
                          }
                        />
                      </td>
                      <td>
                        <span className="mono">{f.field}</span>
                        {dirty ? (
                          <span className="pill pill-warning" style={{ marginLeft: 8 }}>
                            {on ? "enabling" : "disabling"}
                          </span>
                        ) : null}
                      </td>
                      <td className="num">
                        {f.fill_rate === null ? "—" : `${f.fill_rate}%`}
                      </td>
                      <td className="num muted">
                        {f.runs ? `${f.populated}/${f.runs}` : "—"}
                      </td>
                      <td>
                        {/* The distinction that matters when enabling: "this
                            agent emits it and it is usually empty" is a
                            different problem from "this agent has never emitted
                            it at all". */}
                        {f.source === "schema" ? (
                          <span className="muted">declared, never produced</span>
                        ) : f.never_populated ? (
                          <span className="pill pill-critical">always empty</span>
                        ) : (
                          <span className="muted">observed in extractions</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {saveError ? <div className="notice notice-error">{saveError}</div> : null}
        {saved && changed.count === 0 ? (
          <div className="notice">Saved.</div>
        ) : null}

        {isAdmin ? (
          <div className="form-actions">
            {current.size === 0 ? (
              // Mirrors the table's CHECK constraint. Said here in a sentence
              // rather than left to come back as a 400 after a round trip.
              <span className="muted actions-note">
                An agent with no scored fields has no score — enable at least one.
              </span>
            ) : changed.count > 0 ? (
              <span className="muted actions-note">
                {changed.added.length ? `+${changed.added.join(", ")}` : null}
                {changed.added.length && changed.removed.length ? " · " : null}
                {changed.removed.length ? `−${changed.removed.join(", ")}` : null}
              </span>
            ) : (
              <span className="muted actions-note">No changes.</span>
            )}
            <button
              className="btn"
              onClick={() => setDraft(new Set(stored))}
              disabled={changed.count === 0 || saving}
            >
              Reset
            </button>
            <button
              className="btn btn-primary"
              onClick={save}
              disabled={changed.count === 0 || current.size === 0 || saving}
            >
              {saving
                ? "Saving…"
                : `Save${changed.count ? ` ${changed.count} change${changed.count === 1 ? "" : "s"}` : ""}`}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
